// Product tracking: shared types and limits.
// The engine files (types, eventQueue, flusher, tracker) are identical in both products;
// only transport, route patterns, browser hooks and the React glue differ per app.

export type SessionEndReason = 'handoff' | 'signed_out';

// Wire shapes accepted by POST /track (see product_metrics.record_events).
// `closed_tab` is never sent: pagehide also fires on reload and navigation, so a client
// cannot tell a close from a page change. `idle_timeout` belongs to the server.
export type PageViewEvent = {
  type: 'page_view';
  viewId: string;
  sessionId: string;
  sessionStartedAt: string;
  route: string;
  fromRoute?: string;
  enteredAt: string;
};

export type PageLeaveEvent = {
  type: 'page_leave';
  viewId: string;
  sessionId: string;
  sessionStartedAt: string;
  enteredAt: string;
  durationMs: number;
};

export type SessionEndEvent = {
  type: 'session_end';
  sessionId: string;
  sessionStartedAt: string;
  reason: SessionEndReason;
  endedAt: string;
};

export type TrackEvent = PageViewEvent | PageLeaveEvent | SessionEndEvent;

export type SendResult = { ok: boolean; status: number; enabled: boolean };

export type SendFn = (events: TrackEvent[], options: { keepalive: boolean }) => Promise<SendResult>;

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Everything the engine needs from the browser and the host app, injected so it stays testable. */
export interface TrackerDeps {
  uuid: () => string;
  storage: KeyValueStore | null;
  isVisible: () => boolean;
  isAuthenticated: () => boolean;
  /**
   * Pathname (never a query string or hash) to a route PATTERN, or null when it must not be tracked.
   * `kind` is the outcome word a shared page reported (see SUCCESS_KINDS); the function decides what, if anything, to do with it.
   */
  matchRoute: (pathname: string, kind?: unknown) => string | null;
  send: SendFn;
}

export const SESSION_KEY = 'zaq_track_session';
export const QUEUE_KEY = 'zaq_track_queue';

export const LIMITS = {
  /** Flush this long after the first queued event. */
  flushMs: 10_000,
  /** ...or as soon as this many events are waiting. */
  flushAt: 10,
  /** The server accepts at most 20 events per request. */
  maxBatch: 20,
  /** Both backends cap the JSON body at 10 kB; stay well under it. */
  maxBatchBytes: 8_000,
  maxQueue: 200,
  /** The server rejects events older than 24 h. */
  maxEventAgeMs: 23 * 60 * 60 * 1000,
  /** Same 30 minutes as the server's idle reaper. */
  idleMs: 30 * 60 * 1000,
  /** The server rejects a session that started more than 48 h ago. */
  maxSessionAgeMs: 47 * 60 * 60 * 1000,
  /** How long to stay quiet after the server says tracking is off, or the user is signed out. */
  pauseMs: 5 * 60 * 1000,
  backoffMs: [10_000, 30_000, 90_000, 300_000],
} as const;
